import React, { useState, useMemo, useEffect } from "react";
import { Spin } from "antd";
import moment from "moment";
import axios from "axios";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import TableFilterBar from "./components/tables/TableFilterBar";
import TableStatusLegend from "./components/tables/TableStatusLegend";
import TableGrid from "./components/tables/TableGrid";
import KOTGenerationPage from "./KOTGenerationPage"; // Adjust path as needed
import BillGeneration from "./components/billing/BillGeneration"; // Adjust path as needed


const TableBillingOverview = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [zoneFilter, setZoneFilter] = useState(null);
  const [serverFilter, setServerFilter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(moment());
  const [tables, setTables] = useState([]);
  const [mergedTables, setMergedTables] = useState([]);
  const [mergeMode, setMergeMode] = useState(false);
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [isKOTModalOpen, setIsKOTModalOpen] = useState(false);
  const [currentKOTTable, setCurrentKOTTable] = useState(null);
  const [isBillingPanelOpen, setIsBillingPanelOpen] = useState(false);
  const [selectedTableForBilling, setSelectedTableForBilling] = useState(null);
  const [kotItems, setKotItems] = useState([]);
  const [kotCount, setKotCount] = useState(0);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingBill, setLoadingBill] = useState(false);
  const [locations, setLocations] = useState([]);
  const [servers, setServers] = useState([]);
const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const token = localStorage.getItem("token");

  // Fetch tables dynamically
  const fetchTables1 = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/pos-module/tables`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const tablesList = Array.isArray(response.data) ? response.data : [];
      console.log('Fetched tables:', tablesList);
      const mappedTables = tablesList.map(table => ({
        id: table.id,
        name: table.table_no,
        zone: table.location,
        capacity: Number(table.seating_capacity) || 0,
        status: deriveStatus(table),
        guests: getGuests(table),
        server: getServer(table),
        orderStart: table.occupied_at,
        billAmount: calculateBillAmount(table.pending_kots),
        kots: Number(table.pending_kot_count) || 0,
        reservations: table.reservations || [],
        merged_with: Array.isArray(table.merged_tables?.merged_with) ? table.merged_tables.merged_with : [],
        occupied_at: table.occupied_at || null, // Explicitly map occupied_at with fallback
      }));
      setTables(mappedTables);
      const uniqueLocations = [...new Set(tablesList.map(table => table.location))];
      setLocations(uniqueLocations);
      const mergedGroups = [];
      const processedGroups = new Set();
      tablesList.forEach(table => {
        const mergedWith = Array.isArray(table.merged_tables?.merged_with) ? table.merged_tables.merged_with : [];
        if (mergedWith.length > 0 && !processedGroups.has(table.id)) {
          const group = [table.id, ...mergedWith].sort();
          group.forEach(id => processedGroups.add(id));
          mergedGroups.push(group);
        }
      });
      setMergedTables(mergedGroups);
      console.log('Merged groups:', mergedGroups);
      console.log('Mapped tables with kots:', mappedTables.map(t => ({ id: t.id, name: t.name, kots: t.kots }))); // Debug kots
    } catch (error) {
      console.error('Fetch tables error:', error);
      toast.error(error.response?.data?.error || "Failed to fetch tables.");
    } finally {
      setIsLoading(false);
    }
  };


const fetchTables = async () => {
  setIsLoading(true);
  try {
    const response = await axios.get(`${apiUrl}/pos-module/tables`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const tablesList = Array.isArray(response.data) ? response.data : [];
    console.log('Fetched tables:', tablesList);
    const mappedTables = tablesList.map(table => ({
      id: table.id,
      name: table.table_no,
      zone: table.location,
      capacity: Number(table.seating_capacity) || 0,
      status: deriveStatus(table),
      guests: getGuests(table),
      server: getServer(table),
      orderStart: table.occupied_at,
      billAmount: calculateBillAmount(table.pending_kots),
      kots: Array.isArray(table.pending_kots) ? table.pending_kots.map(kot => ({
        kot_id: kot.kot_id,
        kot_number: kot.kot_number,
        items: kot.items || [],
        order_time: kot.order_time,
        is_complementary: kot.is_complementary || false,
        waiter_name: kot.waiter_name || null,
        notes: kot.notes || "",
        status: kot.status || "active",
      })) : [],
      kotCount: Number(table.pending_kot_count) || (Array.isArray(table.pending_kots) ? table.pending_kots.length : 0), // For bill icon
      reservations: table.reservations || [],
      merged_with: Array.isArray(table.merged_tables?.merged_with) ? table.merged_tables.merged_with : [],
      occupied_at: table.occupied_at || null, // Explicitly map occupied_at with fallback
      
    }));
    setTables(mappedTables);
    const uniqueLocations = [...new Set(tablesList.map(table => table.location))];
    setLocations(uniqueLocations);
    const mergedGroups = [];
    const processedGroups = new Set();
    tablesList.forEach(table => {
      const mergedWith = Array.isArray(table.merged_tables?.merged_with) ? table.merged_tables.merged_with : [];
      if (mergedWith.length > 0 && !processedGroups.has(table.id)) {
        const group = [table.id, ...mergedWith].sort();
        group.forEach(id => processedGroups.add(id));
        mergedGroups.push(group);
      }
    });
    setMergedTables(mergedGroups);
    console.log('Merged groups:', mergedGroups);
    console.log('Mapped tables with kots:', mappedTables.map(t => ({ id: t.id, name: t.name, kots: t.kots, kotCount: t.kotCount })));
  } catch (error) {
    console.error('Fetch tables error:', error);
    toast.error(error.response?.data?.error || "Failed to fetch tables.");
  } finally {
    setIsLoading(false);
  }
};


  useEffect(() => {
    fetchTables();
  }, [token]);

  // Fetch menu items
  useEffect(() => {
    const fetchMenuItems = async () => {
      setLoadingMenu(true);
      try {
        const response = await axios.get(`${apiUrl}/pos/items/listings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const rawItems = Array.isArray(response.data) ? response.data : Object.values(response.data);
        const activeItems = rawItems
          .filter((item) => item.is_active && typeof item === "object" && item.menu_id && item.name)
          .map((item) => ({
            menu_id: item.menu_id,
            name: item.name,
            category_name: item.category_name || "Uncategorized",
            price_standard: Number(item.price_standard) || 0,
          }));
        setMenuItems(activeItems);
      } catch (err) {
        console.error('Fetch menu items error:', err);
        toast.error("Failed to fetch menu items");
      } finally {
        setLoadingMenu(false);
      }
    };
    fetchMenuItems();
  }, [token]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await axios.get(`${apiUrl}/pos/categories/listings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const activeCategories = response.data.filter((cat) => cat.is_active && typeof cat === "object" && cat.category_id);
        setCategories(activeCategories);
      } catch (err) {
        console.error('Fetch categories error:', err);
        toast.error("Failed to fetch categories");
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, [token]);

  // Fetch settings
  useEffect(() => {
    const fetchSettings = async () => {
      setLoadingSettings(true);
      try {
        const response = await axios.get(`${apiUrl}/setting-pos`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSettings(response.data.settings || {});
      } catch (err) {
        console.error('Fetch settings error:', err);
        toast.error("Failed to fetch settings");
      } finally {
        setLoadingSettings(false);
      }
    };
    fetchSettings();
  }, [token]);

  // Fetch servers
  useEffect(() => {
    const fetchServers = async () => {
      try {
        const response = await axios.get(`${apiUrl}/pos/waiters/listings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const serverList = response.data.map(waiter => ({
          value: waiter.waiter_id,
          label: waiter.name || `Waiter ${waiter.waiter_id}`,
        }));
        setServers(serverList);
      } catch (err) {
        console.error('Fetch servers error:', err);
        toast.error("Failed to fetch servers");
      }
    };
    fetchServers();
  }, [token]);

  // Update current time
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(moment());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Derive status
  const deriveStatus = (table) => {
    if (table.reservations && table.reservations.length > 0 && table.reservations.some(r => r.status === 'confirmed')) {
      return 'Reserved';
    }
    if (table.current_status === 'Occupied') {
      return 'Occupied';
    }
    if (table.current_status === 'Available' && Number(table.pending_kot_count || 0) > 0) {
      return 'Dirty';
    }
    return 'Available';
  };

  // Get guests
  const getGuests = (table) => {
    if (table.reservations && table.reservations.length > 0) {
      return Number(table.reservations[0].party_size) || 0;
    }
    if (table.pending_kots && table.pending_kots.length > 0) {
      return Number(table.seating_capacity) || 0;
    }
    return 0;
  };

  // Get server
  const getServer = (table) => {
    if (table.pending_kots && table.pending_kots.length > 0) {
      return table.pending_kots[0].waiter_id || 'Unassigned';
    }
    if (table.reservations && table.reservations.length > 0) {
      return table.reservations[0].created_by || 'Unassigned';
    }
    return null;
  };

  // Calculate bill amount
  const calculateBillAmount = (kots) => {
    if (!kots || !Array.isArray(kots)) return 0;
    return kots.reduce((sum, kot) => {
      if (kot.items && Array.isArray(kot.items)) {
        return sum + kot.items.reduce((kotSum, item) => kotSum + (item.qty * (Number(item.price_standard) || 0)), 0);
      }
      return sum;
    }, 0);
  };

  const statusOptions = [
    { value: "Occupied", label: "Occupied" },
    { value: "Reserved", label: "Reserved" },
    { value: "Available", label: "Available" },
    { value: "Dirty", label: "Dirty" },
  ];

  const zoneOptions = locations.map(loc => ({ value: loc, label: loc }));

  const serverOptions = servers;

  const paymentOptions = useMemo(() => {
    const defaultOptions = [
      { value: "Cash", label: "Cash" },
      { value: "UPI", label: "UPI" },
      { value: "Card", label: "Card" },
      { value: "Due", label: "Due" },
    ];
    const settingsPayments = settings?.payment_types?.map(pt => ({
      value: pt.type,
      label: pt.type,
    })) || [];
    return settingsPayments.length > 0 ? settingsPayments : defaultOptions;
  }, [settings]);

  const sourceOptions = useMemo(() => {
    const defaultOptions = [
      { value: "Dine In", label: "Dine In" },
      { value: "Takeaway", label: "Takeaway" },
      { value: "Online", label: "Online" },
    ];
    const settingsSources = settings?.rate_card_sources?.map(src => ({
      value: src.source,
      label: src.source,
    })) || [];
    return [...defaultOptions, ...settingsSources];
  }, [settings]);

  const roomOptions = [];

  const getStatusColor = (status) => {
    switch (status) {
      case "Occupied": return "#ffc107";
      case "Reserved": return "#007bff";
      case "Available": return "#28a745";
      case "Dirty": return "#fd7e14";
      default: return "#6c757d";
    }
  };

// Update handleTableClick to manage occupied_at and trigger re-fetch
const handleTableClick = async (table) => {
  console.log('Table clicked:', table.id, 'Status:', table.status, 'Kots:', table.kots);
  if (mergeMode) {
    if (selectedForMerge.includes(table.id)) {
      setSelectedForMerge(prev => prev.filter(id => id !== table.id));
    } else if (["Available", "Dirty"].includes(table.status)) {
      setSelectedForMerge(prev => [...prev, table.id]);
    } else {
      toast.warning("Can only merge Available or Dirty tables.");
    }
    return;
  }

  if (table.status === "Available") {
    const result = await Swal.fire({
      title: `Mark ${table.name} as Occupied?`,
      text: `This will set ${table.name} to Occupied status.`,
      icon: "question",
      confirmButtonText: "Confirm",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#dc3545",
    });
    if (result.isConfirmed) {
      try {
        await axios.put(
          `${apiUrl}/pos-module/tables/${table.id}`,
          { current_status: "Occupied" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const updatedTables = tables.map(t =>
          t.id === table.id ? { ...t, current_status: "Occupied", status: "Occupied", occupied_at: new Date().toISOString() } : t
        );
        setTables(updatedTables); // Update occupied_at locally
        toast.success(`Table ${table.name} marked as Occupied`);
      } catch (error) {
        console.error('Mark occupied error:', error);
        toast.error(error.response?.data?.error || `Failed to mark ${table.name} as Occupied`);
      }
    }
  } else if (table.status === "Occupied" && Number(table.kots) === 0) {
    const result = await Swal.fire({
      title: `Mark ${table.name} as Available?`,
      text: `This will set ${table.name} back to Available status.`,
      icon: "question",
      confirmButtonText: "Confirm",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#dc3545",
    });
    if (result.isConfirmed) {
      try {
        await axios.put(
          `${apiUrl}/pos-module/tables/${table.id}`,
          { current_status: "Available" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const updatedTables = tables.map(t =>
          t.id === table.id ? { ...t, current_status: "Available", status: "Available", occupied_at: null } : t
        );
        setTables(updatedTables); // Clear occupied_at locally
        toast.success(`Table ${table.name} marked as Available`);
      } catch (error) {
        console.error('Mark available error:', error);
        toast.error(error.response?.data?.error || `Failed to mark ${table.name} as Available`);
      }
    }
  }
};

const handleMerge = async () => {
  if (selectedForMerge.length < 2) {
    toast.error("Select at least 2 tables to merge.");
    return;
  }
  const selectedTables = selectedForMerge.map(id => tables.find(t => t.id === id)).filter(t => t);
  const invalidTables = selectedTables.filter(t => !["Available", "Dirty"].includes(t.status));
  if (invalidTables.length > 0) {
    toast.error(`Cannot merge tables: ${invalidTables.map(t => t.name).join(", ")} are not Available or Dirty.`);
    return;
  }
  const result = await Swal.fire({
    title: "Confirm Merge",
    html: `
      <p>Merge tables ${selectedTables.map(t => t.name).join(", ")} into a single group?</p>
      <p>The group will use the first table's name (${selectedTables[0].name}) as the merged ID.</p>
    `,
    icon: "question",
    confirmButtonText: "Merge",
    showCancelButton: true,
    confirmButtonColor: "#28a745",
    cancelButtonColor: "#dc3545",
  });
  if (result.isConfirmed) {
    try {
      const response = await axios.post(
        `${apiUrl}/pos-module/tables/merge`,
        { table_ids: selectedForMerge },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Merge response:', response.data);
      setMergedTables(prev => [...prev, selectedForMerge.sort()]);
      setSelectedForMerge([]);
      setMergeMode(false);
      toast.success(`Merged tables into ${response.data.table_no}`);
      await fetchTables();
    } catch (error) {
      console.error('Merge error:', error);
      toast.error(error.response?.data?.error || "Failed to merge tables.");
    }
  }
};

const handleUnmerge = async (group) => {
  //alert(`Unmerge triggered for group: ${JSON.stringify(group)}`);
  if (!group || group.length === 0) {
    console.warn('Empty or invalid group:', group, 'Merged tables:', mergedTables);
    return;
  }
  const primaryTable = tables.find(t => t.id === group[0]);
  if (!primaryTable) {
    console.warn('Primary table not found for group:', group);
    return;
  }
  const result = await Swal.fire({
    title: `Unmerge Tables for ${primaryTable.name}`,
    text: `This will separate tables ${group.map(id => tables.find(t => t.id === id)?.name).join(", ")} back to their original positions.`,
    icon: "warning",
    confirmButtonText: "Unmerge",
    showCancelButton: true,
    confirmButtonColor: "#28a745",
    cancelButtonColor: "#dc3545",
  });
  if (result.isConfirmed) {
    try {
      const tableIdToUnmerge = group[0]; // Explicitly define the table_id for clarity
      console.log('Unmerging with table_id:', tableIdToUnmerge); // Debug the sent table_id
      const response = await axios.post(
        `${apiUrl}/pos-module/tables/unmerge`,
        { table_id: tableIdToUnmerge },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Unmerge response:', response.data);
      setMergedTables(prev => prev.filter(g => g[0] !== group[0]));
      toast.success("Tables unmerged successfully.");
      await fetchTables();
    } catch (error) {
      console.error('Unmerge error:', error);
      toast.error(error.response?.data?.error || "Failed to unmerge tables.");
    }
  }
};

  const handleAddKOT = async (table) => {
    if (!["Available", "Occupied"].includes(table.status)) {
      toast.warning(`Cannot add KOT for ${table.name} (status: ${table.status})`);
      return;
    }
    setCurrentKOTTable(table.id);
    setIsKOTModalOpen(true);
  };

const addKOT = async (tableId, roomId, kot) => {
  try {
    const updatedTables = tables.map(table =>
      table.id === tableId
        ? {
            ...table,
            kots: [...(table.kots || []), kot],
            kotCount: (table.kotCount || 0) + 1, // Increment kotCount
            billAmount: (table.billAmount || 0) + kot.items.reduce((sum, item) => sum + (item.qty * (Number(item.price_standard) || 0)), 0),
            status: 'Occupied',
          }
        : table
    );
    setTables(updatedTables);
    toast.success(`KOT added for table ${tables.find(t => t.id === tableId)?.name}`);
    await fetchTables(); // Refresh tables to sync with backend
  } catch (error) {
    console.error('Add KOT error:', error);
    toast.error("Failed to add KOT");
  }
};

  const handleCloseBillingPanel = () => {
    setIsBillingPanelOpen(false);
    setSelectedTableForBilling(null);
    setKotItems([]);
    setKotCount(0);
  };

  const handleOpenBillingPanel = async (tableId) => {
    setLoadingBill(true);
    setSelectedTableForBilling(tableId);
    try {
      const response = await axios.get(`${apiUrl}/pos/orders/table/${tableId}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const billData = response.data;
      if (!billData.kots || !Array.isArray(billData.kots)) {
        throw new Error("Invalid bill data structure: 'kots' not found or not an array");
      }
      const itemsWithKOT = billData.kots.flatMap(kot =>
        (kot.items || []).map(item => ({
          ...item,
          kot_id: kot.kot_id,
          kot_number: kot.kot_number,
          table_id: kot.table_id || tableId,
          amount: item.amount || (item.qty * (Number(item.price_standard) || 0)),
          is_taxable: item.is_taxable || false,
          tax_ids: item.tax_ids || [],
          taxes: item.taxes || [],
          total_tax_rate: item.total_tax_rate || 0,
          tax_name: item.tax_name || "",
          tax_rate: item.tax_rate || 0,
          source_prices: item.source_prices || [],
          price_standard: Number(item.price_standard) || 0,
        }))
      );
      setKotItems(itemsWithKOT);
      setKotCount(billData.kots.length);
      setIsBillingPanelOpen(true);
    } catch (err) {
      console.error("Error fetching bill data:", err);
      toast.error("Failed to fetch bill data: " + err.message);
      setKotItems([]);
      setKotCount(0);
      setIsBillingPanelOpen(false);
    } finally {
      setLoadingBill(false);
    }
  };

  const handleCloseKOTModal = () => {
    setIsKOTModalOpen(false);
    setCurrentKOTTable(null);
  };

  const filteredTables = useMemo(() => {
    let filtered = tables;
    console.log('Filtering tables:', tables, 'with searchTerm:', searchTerm, 'statusFilter:', statusFilter, 'zoneFilter:', zoneFilter, 'serverFilter:', serverFilter); // Debug filtering
    if (searchTerm) {
      filtered = filtered.filter(table =>
        table.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (table.zone && table.zone.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    if (statusFilter) {
      filtered = filtered.filter(table => table.status === statusFilter.value);
    }
    if (zoneFilter) {
      filtered = filtered.filter(table => table.zone === zoneFilter.value);
    }
    if (serverFilter) {
      filtered = filtered.filter(table => table.server === serverFilter.value || table.server === null || !serverFilter.value); // Allow null servers
    }
    console.log('Filtered tables:', filtered); // Debug filtered result
    return filtered.sort((a, b) => a.id.localeCompare(b.id));
  }, [tables, searchTerm, statusFilter, zoneFilter, serverFilter]);

  const groupedTables = useMemo(() => {
    const groups = [];
    const mergedSet = new Set(mergedTables.flat());
    console.log('Processing groupedTables with mergedSet:', mergedSet, 'mergeMode:', mergeMode); // Debug grouping
    let displayTables = mergeMode ? filteredTables.filter(t => ["Available", "Dirty"].includes(t.status) && !mergedSet.has(t.id)) : filteredTables;
    displayTables = displayTables.sort((a, b) => a.id.localeCompare(b.id));
    const unmerged = displayTables.filter(t => !mergedSet.has(t.id));
    groups.push(...unmerged.map(t => [t]).filter(g => g[0])); // Ensure valid groups
    if (!mergeMode) {
      groups.push(...mergedTables.map(groupIds => groupIds.map(id => tables.find(t => t.id === id)).filter(t => t)));
    }
    console.log('Grouped tables:', groups); // Debug grouped result
    return groups;
  }, [filteredTables, mergedTables, mergeMode]);

  const visibleTables = useMemo(() => {
    console.log('Computing visibleTables from groupedTables:', groupedTables); // Debug visible tables
    if (!groupedTables.length) {
      console.warn('No grouped tables to display');
      return [];
    }
    return groupedTables; // Show all tables for simplicity
  }, [groupedTables]);

  if (loadingMenu || loadingCategories || loadingSettings) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  return (
    <div className="ns-rest-root ns-rest-dashboard-root">
      <style>{`
        .ns-rest-dashboard-root {
          width: 100%;
          min-height: 100%;
          height: auto;
          overflow: visible;
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 10px;
          background:
            radial-gradient(circle at 8% 0%, rgba(37, 99, 235, 0.06), transparent 28%),
            #f8fafc;
        }

        .ns-rest-dashboard-heading {
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.10), transparent 34%),
            linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 20px;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
          padding: 13px 15px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          flex-wrap: wrap;
        }

        .ns-rest-dashboard-heading .eyebrow {
          color: #2563eb;
          font-size: 0.68rem;
          letter-spacing: 0.13em;
          text-transform: uppercase;
          font-weight: 950;
          margin-bottom: 4px;
        }

        .ns-rest-dashboard-heading h3 {
          color: #001f5b;
          font-size: 1.08rem;
          line-height: 1.18;
          font-weight: 950;
          margin: 0;
        }

        .ns-rest-dashboard-heading p {
          color: #64748b;
          font-size: 0.78rem;
          line-height: 1.45;
          font-weight: 760;
          margin: 4px 0 0;
        }

        .ns-rest-dashboard-chip-row {
          display: flex;
          align-items: center;
          gap: 7px;
          flex-wrap: wrap;
        }

        .ns-rest-dashboard-chip {
          min-height: 30px;
          border-radius: 999px;
          background: #ffffff;
          color: #334155;
          border: 1px solid rgba(15, 23, 42, 0.08);
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 9px;
          font-size: 0.72rem;
          font-weight: 900;
          white-space: nowrap;
        }

        .ns-rest-dashboard-root > .ns-table-filter-bar {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 20px;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.055);
          padding: 12px;
          flex: 0 0 auto;
          position: relative;
          z-index: 5;
        }

        .ns-rest-dashboard-root > .ns-table-legend {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.045);
          padding: 10px 12px;
          flex: 0 0 auto;
          position: relative;
          z-index: 4;
        }

        .ns-rest-dashboard-root > .ns-table-grid-wrap {
          min-height: 0;
          flex: 1 1 auto;
        }

        @media (max-width: 760px) {
          .ns-rest-dashboard-root {
            padding: 8px;
          }

          .ns-rest-dashboard-heading {
            border-radius: 18px;
            padding: 12px;
          }
        }
      `}</style>


      <TableFilterBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        zoneFilter={zoneFilter}
        setZoneFilter={setZoneFilter}
        serverFilter={serverFilter}
        setServerFilter={setServerFilter}
        statusOptions={statusOptions}
        zoneOptions={zoneOptions}
        serverOptions={serverOptions}
        mergeMode={mergeMode}
        setMergeMode={setMergeMode}
        selectedForMerge={selectedForMerge}
        handleMerge={handleMerge}
        setSelectedForMerge={setSelectedForMerge}
      />
      <TableStatusLegend statusOptions={statusOptions} tables={tables} getStatusColor={getStatusColor} />
      <TableGrid
        visibleTables={visibleTables}
        handleTableClick={handleTableClick}
        handleAddKOT={handleAddKOT}
        handleUnmerge={handleUnmerge}
        handleOpenBillingPanel={handleOpenBillingPanel} // Ensure this prop is passed
        mergeMode={mergeMode}
        selectedForMerge={selectedForMerge}
        getStatusColor={getStatusColor}
      />
      {isBillingPanelOpen && (
        <BillGeneration
          isOpen={isBillingPanelOpen}
          onClose={handleCloseBillingPanel}
          entityId={selectedTableForBilling}
          tableNo={tables.find(t => t.id === selectedTableForBilling)?.name} // Add table_no
          entityType="table"
          kotItems={kotItems}
          kotCount={kotCount}
          paymentOptions={paymentOptions}
          sourceOptions={sourceOptions}
          settings={settings}
          roomOptions={roomOptions}
          fetchData={fetchTables}
          apiUrl={apiUrl}
          token={token}
        />
      )}
{isKOTModalOpen && currentKOTTable && (
  <KOTGenerationPage
    key={`table-kot-${currentKOTTable}`}
    tableId={currentKOTTable}
    onClose={handleCloseKOTModal}
    menuItems={menuItems}
    categories={categories}
    tableNo={tables.find((t) => t.id === currentKOTTable)?.name}
    settings={settings}
    tables={tables}
    rooms={[]}
    setTables={setTables}
    setRooms={() => {}}
    addKOT={addKOT}
    calledFrom="table"
    servers={servers}
  />
)}
    </div>
  );
};

export default TableBillingOverview;